import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import {
  showcaseWorkflow,
  SHOWCASE_WORKFLOW_ERROR_STATES,
} from "../../../../workflows/workflowDefinitions";
import { DesignShowcaseViewStore } from "../../store/DesignShowcaseViewStore";
import { useRootStore } from "../../../../store/contexts";
import { ShowItemsCompact } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";
import { ShowMultiLine } from "../../../common/controls";
import { ShowcaseStatus } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { ShowReferenceSet } from "../../../common/controls";
import { WorkflowRoadmap } from "../../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { ShowIntegerChoices } from "../../../common/controls";
import { ShowDate } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";
import { ShowBoolean } from "../../../common/controls";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { InputHtmlString } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";

export type DesignShowcaseViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  linesRows: CrudRows;
  linesVisibleColumns: GridColumn[];
  linesSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  codeValue?: { displayId: string; id: string } | null;
  code: string;
  nameValue?: { displayId: string; id: string } | null;
  name: string;
  summaryValue?: { displayId: string; id: string } | null;
  summary: string;
  statusOptions: { id: string; displayId: string }[];
  status: ShowcaseStatus;
  categoryValue: { displayId: string; id: string } | null;
  viewFormCategory?: () => void;
  relatedCategoriesValues: { displayId: string; id: string }[];
  appliedStatusesOptions: { id: string; displayId: string }[];
  appliedStatuses: ShowcaseStatus[];
  lifecycleValue: string;
  quantityValue?: { displayId: string; id: string } | null;
  quantity: number;
  durationInYearsValue?: { displayId: string; id: string } | null;
  durationInYears: number | undefined;
  amountValue?: { displayId: string; id: string } | null;
  amount: number;
  ratioValue?: { displayId: string; id: string } | null;
  ratio: number | undefined;
  startDateValue?: { displayId: string; id: string } | null;
  startDate: string | null;
  expiresAtValue?: { displayId: string; id: string } | null;
  expiresAt: Date | null;
  registeredAtValue?: { displayId: string; id: string } | null;
  registeredAt: Date | null;
  activeValue?: { displayId: string; id: string } | null;
  active: boolean;
  highlightedValue?: { displayId: string; id: string } | null;
  highlighted: boolean;
  phoneValue?: { displayId: string; id: string } | null;
  phone: string;
  taxIdValue?: { displayId: string; id: string } | null;
  taxId: string;
  attachmentFiles: CrudRows;
  attachmentViewAction?: IMenuAction[];
  anyFileFiles: CrudRows;
  anyFileViewAction?: IMenuAction[];
  avatarFiles: CrudRows;
  avatarViewAction?: IMenuAction[];
  keywordsValue?: { displayId: string; id: string } | null;
  keywords: string[];
  bodyValue?: { displayId: string; id: string } | null;
  body: string;
  metadataValue?: { displayId: string; id: string } | null;
  metadata: string;
  internalNoteValue?: { displayId: string; id: string } | null;
  internalNote: string;
  serverLabelValue?: { displayId: string; id: string } | null;
  serverLabel: string;
  designShowcaseViewStore: DesignShowcaseViewStore;
};

export const DesignShowcaseViewForm: React.FC<DesignShowcaseViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      linesRows,
      linesVisibleColumns,
      linesSetColumnWiths,
      code = "",
      name = "",
      summary = "",
      statusOptions = [],
      status = "" as ShowcaseStatus,
      categoryValue,
      viewFormCategory = () => {},
      relatedCategoriesValues,
      appliedStatusesOptions = [],
      appliedStatuses = [] as ShowcaseStatus[],
      lifecycleValue = "",
      quantity = 0,
      durationInYears = undefined,
      amount = 0,
      ratio = 0,
      startDate = null,
      expiresAt = null,
      registeredAt = null,
      active = false,
      highlighted = false,
      phone = "",
      taxId = "",
      attachmentFiles,
      attachmentViewAction,
      anyFileFiles,
      anyFileViewAction,
      avatarFiles,
      avatarViewAction,
      keywords = [],
      body = "",
      metadata = "{}",
      internalNote = "",
      serverLabel = "",
      designShowcaseViewStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore, authStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="null"
              title={langStore.groupDesignShowcaseLabel}
              collapsible={true}
            >
              <FormControl fullWidth>
                <ShowItemsCompact
                  rows={linesRows}
                  showLabel={false}
                  label={langStore.DesignShowcaseLinesLabel}
                  columns={linesVisibleColumns}
                  setColumnWidths={linesSetColumnWiths}
                  entityName="ShowcaseLine"
                  enableDownload={true}
                  onRowClick={(rowId) =>
                    rootStore.openSubentityModal("showcaseLine", rowId)
                  }
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="identification"
              title={langStore.groupDesignShowcaseIdentificationLabel}
              collapsible={true}
              expanded={designShowcaseViewStore.getCardState("identification")}
              toggleExpand={() =>
                designShowcaseViewStore.toggleCardState("identification")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={code}
                  label={langStore.DesignShowcaseCodeLabel}
                />
                <ShowMultiLine
                  value={name}
                  label={langStore.DesignShowcaseNameLabel}
                />
                <ShowMultiLine
                  value={summary}
                  label={langStore.DesignShowcaseSummaryLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="classification"
              title={langStore.groupDesignShowcaseClassificationLabel}
              collapsible={true}
              expanded={designShowcaseViewStore.getCardState("classification")}
              toggleExpand={() =>
                designShowcaseViewStore.toggleCardState("classification")
              }
            >
              <FormControl fullWidth>
                <ShowString
                  value={
                    status
                      ? statusOptions.find(
                          (o: { id: string }) => o.id === status
                        )?.displayId || status
                      : ""
                  }
                  label={langStore.DesignShowcaseStatusLabel}
                />
                <ShowReference
                  label={langStore.DesignShowcaseCategoryLabel}
                  selectedValue={langStore.displayIdByLang(
                    categoryValue?.displayId
                  )}
                  onView={viewFormCategory}
                />
                <ShowReferenceSet
                  label={langStore.DesignShowcaseRelatedCategoriesLabel}
                  selectedValues={relatedCategoriesValues}
                />
                <ShowReferenceSet
                  label={langStore.DesignShowcaseAppliedStatusesLabel}
                  selectedValues={appliedStatuses.map((v: ShowcaseStatus) => ({
                    id: v,
                    displayId:
                      appliedStatusesOptions.find(
                        (o: { id: string }) => o.id === v
                      )?.displayId || v,
                  }))}
                />
                <WorkflowRoadmap
                  workflow={showcaseWorkflow}
                  currentState={lifecycleValue}
                  labels={langStore.DesignShowcaseLifecycleStateLabels}
                  transitions={[]}
                  onTransition={() => {}}
                  isProcessing={false}
                  errorStates={SHOWCASE_WORKFLOW_ERROR_STATES}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="numbers"
              title={langStore.groupDesignShowcaseNumbersLabel}
              collapsible={true}
              expanded={designShowcaseViewStore.getCardState("numbers")}
              toggleExpand={() =>
                designShowcaseViewStore.toggleCardState("numbers")
              }
            >
              <FormControl fullWidth>
                <ShowNumber
                  value={quantity}
                  label={langStore.DesignShowcaseQuantityLabel}
                />
                <ShowIntegerChoices
                  value={durationInYears}
                  label={langStore.DesignShowcaseDurationInYearsLabel}
                  unitSingular={"año"}
                  unitPlural={"años"}
                  nullText={langStore.DesignShowcaseDurationInYearsNullText}
                />
                <ShowNumber
                  value={amount}
                  label={langStore.DesignShowcaseAmountLabel}
                  decimalScale={2}
                  prefix={"$"}
                  thousandSeparator={","}
                  fixedDecimalScale={true}
                />
                <ShowNumber
                  value={ratio}
                  label={langStore.DesignShowcaseRatioLabel}
                  decimalScale={2}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="dates"
              title={langStore.groupDesignShowcaseDatesLabel}
              collapsible={true}
              expanded={designShowcaseViewStore.getCardState("dates")}
              toggleExpand={() =>
                designShowcaseViewStore.toggleCardState("dates")
              }
            >
              <FormControl fullWidth>
                <ShowDate
                  value={startDate}
                  label={langStore.DesignShowcaseStartDateLabel}
                  locale={langStore.timeFormat}
                />
                <ShowDateTime
                  value={expiresAt}
                  label={langStore.DesignShowcaseExpiresAtLabel}
                />
                <ShowDateTime
                  value={registeredAt}
                  label={langStore.DesignShowcaseRegisteredAtLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="flags"
              title={langStore.groupDesignShowcaseFlagsLabel}
              collapsible={true}
              expanded={designShowcaseViewStore.getCardState("flags")}
              toggleExpand={() =>
                designShowcaseViewStore.toggleCardState("flags")
              }
            >
              <FormControl fullWidth>
                <ShowBoolean
                  value={active}
                  label={langStore.DesignShowcaseActiveLabel}
                />
                <ShowBoolean
                  value={highlighted}
                  label={langStore.DesignShowcaseHighlightedLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="contact"
              title={langStore.groupDesignShowcaseContactLabel}
              collapsible={true}
              expanded={designShowcaseViewStore.getCardState("contact")}
              toggleExpand={() =>
                designShowcaseViewStore.toggleCardState("contact")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={phone}
                  label={langStore.DesignShowcasePhoneLabel}
                />
                <ShowMultiLine
                  value={taxId}
                  label={langStore.DesignShowcaseTaxIdLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="documents"
              title={langStore.groupDesignShowcaseDocumentsLabel}
              collapsible={true}
              expanded={designShowcaseViewStore.getCardState("documents")}
              toggleExpand={() =>
                designShowcaseViewStore.toggleCardState("documents")
              }
            >
              <FormControl fullWidth>
                <ShowFilesCompact
                  rows={attachmentFiles}
                  showLabel={true}
                  label={langStore.DesignShowcaseAttachmentLabel}
                  menuActions={attachmentViewAction}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />
                <ShowFilesCompact
                  rows={anyFileFiles}
                  showLabel={true}
                  label={langStore.DesignShowcaseAnyFileLabel}
                  menuActions={anyFileViewAction}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />
                <ShowFilesCompact
                  rows={avatarFiles}
                  showLabel={true}
                  label={langStore.DesignShowcaseAvatarLabel}
                  menuActions={avatarViewAction}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="richContent"
              title={langStore.groupDesignShowcaseRichContentLabel}
              collapsible={true}
              expanded={designShowcaseViewStore.getCardState("richContent")}
              toggleExpand={() =>
                designShowcaseViewStore.toggleCardState("richContent")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={keywords}
                  label={langStore.DesignShowcaseKeywordsLabel}
                />
                <InputHtmlString
                  value={body}
                  onChange={() => {}}
                  label={langStore.DesignShowcaseBodyLabel}
                  readOnly={true}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="advanced"
              title={langStore.groupDesignShowcaseAdvancedLabel}
              collapsible={true}
              expanded={designShowcaseViewStore.getCardState("advanced")}
              toggleExpand={() =>
                designShowcaseViewStore.toggleCardState("advanced")
              }
            >
              <FormControl fullWidth>
                <InputCodeEditor
                  value={metadata}
                  onChange={() => {}}
                  label={langStore.DesignShowcaseMetadataLabel}
                  language="json"
                  readOnly={true}
                />
                {["SysAdmin"].some((role) => authStore.hasRole(role)) && (
                  <ShowMultiLine
                    value={internalNote}
                    label={langStore.DesignShowcaseInternalNoteLabel}
                  />
                )}

                <ShowMultiLine
                  value={serverLabel}
                  label={langStore.DesignShowcaseServerLabelLabel}
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
